import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useLeadLists } from "@/hooks/use-lead-lists";
import { useLeadListStats } from "@/hooks/use-lead-list-actions";
import { usePoolOverview } from "@/hooks/use-lead-pools";
import { useUpdateLeadList } from "@/hooks/use-lead-list-actions";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatPercent } from "@/lib/format";
import { listYield } from "@/lib/lead-stats-calc";
import LeadListFormDialog from "./LeadListFormDialog";
import NewLeadDialog from "./NewLeadDialog";

const SOURCE_LABELS = { csv: "CSV", brreg_search: "Brønnøysund", agent: "Agent", manual: "Manuelt", sheet: "Arket" } as const;

const pct = (v: number | null) => (v === null ? "–" : formatPercent(Math.round(v * 100)));

/**
 * Poolene (Leadlister, VIB-3050 skjerm 3): hvor leadene er nå (ledig, i køer, hviler, møter, kunde,
 * avvist), ringt-andel og utbytte (andel i pipe eller kunde av de ringte, se listYield).
 * Navnet åpner leadene i poolen.
 */
export default function LeadListsTable({ onOpenPool }: { onOpenPool: (listId: string) => void }) {
  const { data: lists, isLoading } = useLeadLists();
  const { data: stats } = useLeadListStats();
  const update = useUpdateLeadList();
  const [formOpen, setFormOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const { data: overview } = usePoolOverview();
  const statsById = new Map((stats ?? []).map((s) => [s.list_id, s]));
  const countsById = new Map((overview ?? []).map((c) => [c.list_id, c]));

  return (
    <section aria-label="Leadlister" className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold">Leadlister</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAdding(true)}>
            <SrIcon name="plus" className="mr-2 h-4 w-4" />
            Nytt lead
          </Button>
          <Button onClick={() => setFormOpen(true)}>Ny liste</Button>
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : (lists ?? []).length === 0 ? (
        <p className="border border-border p-6 text-sm text-muted-foreground">
          Ingen lister ennå. Lag en liste fra en CSV-fil eller et Brønnøysund-søk, så kan møtebookerne begynne å ringe.
        </p>
      ) : (
        <div className="overflow-x-auto border-[1.5px] border-foreground">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <th className="p-2">Liste</th>
                <th className="p-2">Kilde</th>
                <th className="p-2 text-right">Ledig</th>
                <th className="p-2 text-right">I køer</th>
                <th className="p-2 text-right">Hviler</th>
                <th className="p-2 text-right">Møter</th>
                <th className="p-2 text-right">Kunde</th>
                <th className="p-2 text-right">Avvist</th>
                <th className="p-2 text-right">Ringt</th>
                <th className="p-2 text-right">Utbytte</th>
                <th className="p-2">Aktiv</th>
              </tr>
            </thead>
            <tbody>
              {(lists ?? []).map((l) => {
                const s = statsById.get(l.id);
                const c = countsById.get(l.id);
                const low = l.low_water != null && c != null && c.free < l.low_water;
                const y = s ? listYield({ total: Number(s.total), called_count: Number(s.called_count), in_pipe_count: Number(s.in_pipe_count), customer_count: Number(s.customer_count) }) : null;
                return (
                  <tr key={l.id} className="border-b border-border/50">
                    <td className="p-2">
                      <button type="button" className="font-medium underline-offset-2 hover:underline" onClick={() => onOpenPool(l.id)}>
                        {l.name}
                      </button>
                      {low && <span className="ml-2 border border-foreground px-1.5 py-0.5 text-xs">Under lavvann</span>}
                    </td>
                    <td className="p-2">{SOURCE_LABELS[l.source]}</td>
                    <td className="p-2 text-right tabular-nums">{c?.free ?? 0}</td>
                    <td className="p-2 text-right tabular-nums">{c?.queued ?? 0}</td>
                    <td className="p-2 text-right tabular-nums">{c?.resting ?? 0}</td>
                    <td className="p-2 text-right tabular-nums">{c?.meeting ?? 0}</td>
                    <td className="p-2 text-right tabular-nums">{c?.customer ?? 0}</td>
                    <td className="p-2 text-right tabular-nums">{c?.rejected ?? 0}</td>
                    <td className="p-2 text-right tabular-nums">{pct(y?.calledShare ?? null)}</td>
                    <td className="p-2 text-right tabular-nums">{pct(y?.yieldShare ?? null)}</td>
                    <td className="p-2">
                      <Switch
                        checked={l.is_active}
                        disabled={update.isPending}
                        aria-label={`${l.is_active ? "Deaktiver" : "Aktiver"} ${l.name}`}
                        onCheckedChange={(is_active) => update.mutate({ id: l.id, patch: { is_active } })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <LeadListFormDialog open={formOpen} onOpenChange={setFormOpen} />
      <NewLeadDialog open={adding} onOpenChange={setAdding} />
    </section>
  );
}
